/* =========================================================================
   main-product-shieldtech.js
   Interaction layer for the ShieldTech PDP section. Framework-free.

   Responsibilities:
     1. Gallery       — click a thumbnail to swap the main image.
     2. Variant picker — click a color option to select the matching
                         Shopify variant. Updates the hidden [name=id]
                         input on the <product-form>, the URL, price,
                         savings, ATC label, and — if the variant has a
                         featured image — the gallery.
     3. Promo count   — live-tick a "Xd YYh MMm" timer from a target date.
     4. Add-to-cart    — piggy-back on the theme's <product-form> submit
                         (no code needed — theme's product-form.js opens
                         the cart drawer). We only listen for the
                         cart:refresh event so we can update the ATC
                         label if desired.

   The section can be initialised multiple times per page (unlikely for a
   main-product, but possible) — each block is scoped by its data-section
   attribute.
   ========================================================================= */

(function () {
  'use strict';

  function init(root) {
    if (!root || root.dataset.stInit === '1') return;
    root.dataset.stInit = '1';

    /* ---------- 1) Gallery ---------- */
    const mainImg  = root.querySelector('[data-st="main-image"]');
    const thumbs   = root.querySelectorAll('[data-st="thumb"]');

    function selectThumb(btn) {
      if (!btn) return;
      const src = btn.getAttribute('data-src');
      if (!src || !mainImg) return;

      mainImg.classList.remove('animate-fade-in');
      void mainImg.offsetWidth;               // restart the animation
      mainImg.src = src;
      const alt = btn.getAttribute('data-alt');
      if (alt) mainImg.alt = alt;
      mainImg.classList.add('animate-fade-in');

      thumbs.forEach(function (t) {
        t.setAttribute('aria-pressed', 'false');
        t.classList.remove('border-neutral-900');
        t.classList.add('border-transparent', 'opacity-70', 'hover:opacity-100');
      });
      btn.setAttribute('aria-pressed', 'true');
      btn.classList.add('border-neutral-900');
      btn.classList.remove('border-transparent', 'opacity-70', 'hover:opacity-100');
    }

    thumbs.forEach(function (btn) {
      btn.addEventListener('click', function () { selectThumb(btn); });
    });

    /* ---------- 2) Variant picker ---------- */
    const optionButtons = root.querySelectorAll('[data-st="variant-option"]');
    const colorLabel    = root.querySelector('[data-st="color-label"]');
    const priceEl       = root.querySelector('[data-st="price-current"]');
    const compareEl     = root.querySelector('[data-st="price-compare"]');
    const savingsEl     = root.querySelector('[data-st="price-savings"]');
    const atcLabel      = root.querySelector('[data-st="atc-label"]');
    const stickyPriceEl = root.querySelector('[data-st="sticky-price"]');
    const stickyCompareEl = root.querySelector('[data-st="sticky-compare"]');
    const variantIdInput = root.querySelector('input[name="id"]');
    const buyButton     = root.querySelector('[data-st="atc-button"]');

    // Variants JSON is dumped by Liquid as a script tag.
    let variants = [];
    const variantsScript = root.querySelector('[data-st="variants-json"]');
    if (variantsScript) {
      try { variants = JSON.parse(variantsScript.textContent); }
      catch (e) { console.warn('ShieldTech PDP: variants parse failed', e); }
    }

    // Money formatter honoring shop's format (falls back to $X.XX).
    const moneyFormat = (root.dataset.moneyFormat || '${{amount}}');
    function formatMoney(cents) {
      if (cents == null) return '';
      const amount = (cents / 100).toFixed(2).replace(/\.00$/, '');
      // Very small subset of Shopify's format; enough for {{amount}} and {{amount_no_decimals}}.
      return moneyFormat
        .replace(/\{\{\s*amount\s*\}\}/g, (cents / 100).toFixed(2))
        .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, Math.round(cents / 100).toString())
        .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, (cents / 100).toFixed(2).replace('.', ','))
        .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, Math.round(cents / 100).toString());
    }

    function applyVariant(variant) {
      if (!variant || !variantIdInput) return;
      variantIdInput.value = variant.id;

      // Update URL ?variant= (non-destructive)
      try {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());
      } catch (e) {}

      // Update prices
      if (priceEl)   priceEl.textContent   = formatMoney(variant.price);
      if (stickyPriceEl) stickyPriceEl.textContent = formatMoney(variant.price);

      if (compareEl) {
        if (variant.compare_at_price && variant.compare_at_price > variant.price) {
          compareEl.textContent = formatMoney(variant.compare_at_price);
          compareEl.style.display = '';
          if (stickyCompareEl) {
            stickyCompareEl.textContent = formatMoney(variant.compare_at_price);
            stickyCompareEl.style.display = '';
          }
        } else {
          compareEl.style.display = 'none';
          if (stickyCompareEl) stickyCompareEl.style.display = 'none';
        }
      }
      if (savingsEl) {
        if (variant.compare_at_price && variant.compare_at_price > variant.price) {
          const saved = variant.compare_at_price - variant.price;
          const prefix = savingsEl.getAttribute('data-prefix') || 'You save';
          savingsEl.textContent = prefix + ' ' + formatMoney(saved);
          savingsEl.style.display = '';
        } else {
          savingsEl.style.display = 'none';
        }
      }

      // Update ATC label
      if (atcLabel) {
        const template = atcLabel.getAttribute('data-template') || 'Add to cart';
        if (!variant.available) {
          atcLabel.textContent = 'Sold out';
          if (buyButton) buyButton.setAttribute('disabled', 'disabled');
        } else {
          const showPrice = atcLabel.getAttribute('data-show-price') === '1';
          atcLabel.textContent = showPrice
            ? template + ' — ' + formatMoney(variant.price)
            : template;
          if (buyButton) buyButton.removeAttribute('disabled');
        }
      }

      // Swap gallery to variant's featured image if present
      if (variant.featured_image && variant.featured_image.src && mainImg) {
        const src = variant.featured_image.src;
        // Find a matching thumb if any and simulate its selection for the border update.
        let matched = false;
        thumbs.forEach(function (t) {
          if (t.getAttribute('data-src') === src ||
              (t.getAttribute('data-src') || '').indexOf(src.split('?')[0]) !== -1) {
            selectThumb(t);
            matched = true;
          }
        });
        if (!matched) {
          mainImg.classList.remove('animate-fade-in');
          void mainImg.offsetWidth;
          mainImg.src = src;
          mainImg.classList.add('animate-fade-in');
        }
      }
    }

    optionButtons.forEach(function (btn) {
      btn.addEventListener('click', function () {
        const vid = parseInt(btn.getAttribute('data-variant-id'), 10);
        const variant = variants.find(function (v) { return v.id === vid; });

        optionButtons.forEach(function (o) {
          o.setAttribute('aria-pressed', 'false');
          o.classList.remove('border-neutral-900', 'bg-neutral-900/5');
          o.classList.add('border-black/10', 'hover:border-neutral-400');
          const sw = o.querySelector('[data-st="swatch"]');
          if (sw) {
            sw.classList.remove('border-neutral-900', 'ring-2', 'ring-offset-1', 'ring-neutral-900');
            sw.classList.add('border-black/20');
          }
        });
        btn.setAttribute('aria-pressed', 'true');
        btn.classList.add('border-neutral-900', 'bg-neutral-900/5');
        btn.classList.remove('border-black/10', 'hover:border-neutral-400');
        const sw = btn.querySelector('[data-st="swatch"]');
        if (sw) {
          sw.classList.add('border-neutral-900', 'ring-2', 'ring-offset-1', 'ring-neutral-900');
          sw.classList.remove('border-black/20');
        }
        if (colorLabel) colorLabel.textContent = btn.getAttribute('data-option-name') || '';

        if (variant) applyVariant(variant);
      });
    });

    /* ---------- 3) Promo countdown ---------- */
    const countdownEl = root.querySelector('[data-st="countdown"]');
    if (countdownEl) {
      const targetIso = countdownEl.getAttribute('data-target');
      const target = targetIso ? new Date(targetIso).getTime() : 0;
      function fmt(ms) {
        if (ms <= 0) return countdownEl.getAttribute('data-expired-text') || 'Expired';
        const totalMinutes = Math.floor(ms / 60000);
        const d = Math.floor(totalMinutes / (60 * 24));
        const h = Math.floor((totalMinutes % (60 * 24)) / 60);
        const m = totalMinutes % 60;
        return d + 'd ' + String(h).padStart(2, '0') + 'h ' + String(m).padStart(2, '0') + 'm';
      }
      if (target) {
        const tick = function () { countdownEl.textContent = fmt(target - Date.now()); };
        tick();
        setInterval(tick, 30 * 1000);
      }
    }

    /* ---------- 4) Cart event bridge (optional feedback) ---------- */
    // If the theme fires a 'cart:refresh' or the product-form completed successfully,
    // we don't need to do anything — the theme's cart-drawer opens itself.
    // We just add a light toast if there's no cart drawer present at all
    // (edge case — e.g. cart-drawer disabled site-wide).
    const productForm = root.querySelector('product-form form');
    if (productForm) {
      productForm.addEventListener('submit', function () {
        if (!document.querySelector('cart-drawer')) {
          // Fallback toast for no-drawer setups
          setTimeout(function () {
            const t = document.createElement('div');
            t.className = 'st-toast success';
            t.textContent = 'Added to cart';
            document.body.appendChild(t);
            setTimeout(function () {
              t.style.transition = 'opacity .3s, transform .3s';
              t.style.opacity = '0';
              t.style.transform = 'translateY(-8px)';
              setTimeout(function () { t.remove(); }, 320);
            }, 2200);
          }, 400);
        }
      });
    }
  }

  function initAll() {
    document.querySelectorAll('[data-st-section="main-product-shieldtech"]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  // Re-init after Shopify's section rendering API updates
  document.addEventListener('shopify:section:load', function (e) {
    const el = e.target.querySelector('[data-st-section="main-product-shieldtech"]');
    if (el) init(el);
  });
})();
